import { useSetLocale } from "@refinedev/core";
import * as Sentry from "@sentry/react";
import {
  memo,
  useEffect,
  useLayoutEffect,
  useRef,
  useSyncExternalStore,
} from "react";
import { useSearchParams } from "react-router-dom";
import { getCookieConsent, subscribeCookieConsent } from "@libertify/ui";
import { useBoundStore } from "@/store";
import { authService } from "../services/authService";
import { persistOnboardingProperties, useAnalytics } from "hooks/useAnalytics";
import { useGetActiveSubscriptions } from "#src/hooks";
import { addDateTz, normalizeLocale } from "pages/media/utils";
import { useSession } from "hooks/useSession";
import { useAuthActions } from "hooks/useAuthActions";

/**
 * Reactive hook for auth state — re-renders when token is set/cleared.
 * Uses useSyncExternalStore so the component sees token changes immediately,
 * even without a route change or other hook-driven re-render.
 */
const useHasValidToken = () =>
  useSyncExternalStore(
    (cb) => authService.subscribe(cb),
    () => authService.isAuthenticated()
  );

// Re-renders on Accept/Reject, also when answered in another tab
const useHasAnalyticsConsent = () =>
  useSyncExternalStore(subscribeCookieConsent, getCookieConsent) === "true";

/**
 * AuthInitializer handles post-authentication initialization tasks:
 * - Detects login transitions and invalidates stale queries
 * - Initializes analytics (identify user, group organization)
 * - Configures user locale from preferences
 * - Sets up Sentry user context
 * - Restores organization selection from localStorage/URL
 *
 * Unlike the old AuthManager, it does NOT fetch user/org data -
 * that's handled automatically by React Query via useSession.
 *
 * IMPORTANT: Memoized to prevent re-renders when Refine contexts update.
 * This component has no props, so it only re-renders when hooks force updates.
 */
const AuthInitializerComponent = () => {
  // Reactive: re-renders when authService.setToken/clearToken/logout fires subscribers
  const hasValidToken = useHasValidToken();

  // Track previous auth state to detect login transitions
  // IMPORTANT: Initialize with current token state to avoid clearing cache on first render
  const wasAuthenticated = useRef(hasValidToken);

  // Track auth state changes
  useEffect(() => {
    wasAuthenticated.current = hasValidToken;
  }, [hasValidToken]);

  const { identifyUser, group } = useAnalytics();
  const hasAnalyticsConsent = useHasAnalyticsConsent();
  const changeLocale = useSetLocale();
  const { user, organizations, currentOrganization, isLoading } = useSession();
  const { selectOrganization } = useAuthActions();
  const [searchParams] = useSearchParams();
  const urlOrganizationId = searchParams.get("organizationId");

  const {
    activeSubscriptionDetails: activeSubscription,
    isLoading: isLoadingSubscription,
  } = useGetActiveSubscriptions();

  // Track the last organization ID that was grouped to prevent duplicate calls
  const lastGroupedOrgId = useRef<string | null>(null);

  // Track if user has been initialized to prevent duplicate initialization
  const hasInitializedUser = useRef(false);
  const lastInitializedUserId = useRef<number | null>(null);

  // Track the last identified user; cleared without consent so a re-accept identifies again
  const lastIdentifiedUserId = useRef<number | null>(null);

  // Initialize user context (locale, Sentry)
  // Use useLayoutEffect for synchronous execution - critical for menu rendering
  useLayoutEffect(() => {
    // Reset flag if user changed or logged out
    if (!user) {
      hasInitializedUser.current = false;
      lastInitializedUserId.current = null;
      return;
    }

    // Check if we need to initialize for this user (new user or not initialized yet)
    const needsInit =
      !hasInitializedUser.current || lastInitializedUserId.current !== user.id;

    if (!isLoading && needsInit) {
      hasInitializedUser.current = true;
      lastInitializedUserId.current = user.id;

      // Restore user language
      changeLocale(normalizeLocale(user.language ?? "en"));

      // Initialize Sentry
      Sentry.setUser({
        id: user.id.toString(),
      });
    }
  }, [user, isLoading, changeLocale]);

  // Initialize analytics once the user is loaded and has accepted cookies
  useEffect(() => {
    if (!user || !hasAnalyticsConsent) {
      lastIdentifiedUserId.current = null;
      return;
    }
    if (isLoading || lastIdentifiedUserId.current === user.id) return;
    lastIdentifiedUserId.current = user.id;

    // Pass organization IDs for Mixpanel group analytics
    identifyUser(user.id.toString(), {
      country: user.country ?? undefined,
      created: user.created ?? undefined,
      first_name: user.first_name ?? undefined,
      last_name: user.last_name ?? undefined,
      email: user.email,
      organizations: user.organizations,
    });
  }, [user, isLoading, hasAnalyticsConsent, identifyUser]);

  // Restore organization from localStorage/URL on each login.
  // Reset when token disappears so a new user gets fresh org restoration.
  const hasRestoredOrg = useRef(false);
  useEffect(() => {
    if (!hasValidToken) {
      hasRestoredOrg.current = false;
    }
  }, [hasValidToken]);

  useEffect(() => {
    if (organizations.length > 0 && !isLoading && !hasRestoredOrg.current) {
      hasRestoredOrg.current = true;

      const storedOrgId =
        urlOrganizationId || useBoundStore.getState().selectedOrganizationId;

      // If user has selected an organization before, restore it
      if (storedOrgId && storedOrgId !== "null") {
        const org = organizations.find(
          (organization) => organization.id === storedOrgId
        );

        // Only call selectOrganization if the org is different from current
        // to avoid unnecessary Zustand updates and re-renders
        if (org && org.id !== currentOrganization?.id) {
          selectOrganization(org.id);
        }
      } else {
        // No stored org — prefer one the user actually belongs to.
        // For admin users, /api/users/me/organizations returns all orgs they
        // can access; user.organizations is the subset that's actually theirs.
        const ownOrgId = user?.organizations?.find((id) =>
          organizations.some((org) => org.id === id)
        );
        const fallback =
          organizations.find((org) => org.id === ownOrgId) ?? organizations[0];
        if (fallback) selectOrganization(fallback.id);
      }
    }
  }, [
    organizations,
    isLoading,
    selectOrganization,
    urlOrganizationId,
    currentOrganization,
    user,
  ]);

  // Track active subscription event
  useEffect(() => {
    if (user && !isLoading && !isLoadingSubscription && activeSubscription) {
      if (hasAnalyticsConsent) {
        identifyUser(user.id.toString(), undefined, {
          plan: activeSubscription.plan,
          plan_type: activeSubscription.plan_type,
          billing_interval: activeSubscription.billing_interval,
        });
      }
      persistOnboardingProperties({
        plan: activeSubscription.plan,
        plan_type: activeSubscription.plan_type,
        billing_interval: activeSubscription.billing_interval,
      });
    }
  }, [
    identifyUser,
    isLoading,
    isLoadingSubscription,
    activeSubscription,
    user,
    hasAnalyticsConsent,
  ]);

  // Track organization in analytics when it changes or once cookies are accepted
  useEffect(() => {
    if (!hasAnalyticsConsent) {
      lastGroupedOrgId.current = null;
      return;
    }
    const orgId = currentOrganization?.id;

    if (orgId && !isLoading && lastGroupedOrgId.current !== orgId) {
      lastGroupedOrgId.current = orgId;
      group(orgId, {
        name: currentOrganization.name,
        createdAt: addDateTz(currentOrganization.created),
      });
    }
  }, [currentOrganization, isLoading, group, hasAnalyticsConsent]);

  return null;
};

// Export memoized version to prevent unnecessary re-renders from Refine context updates
export const AuthInitializer = memo(AuthInitializerComponent);
